import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {EnergyCommunityNetwork} from '../entities';
import {MatTreeFlatDataSource, MatTreeFlattener} from '@angular/material/tree';
import {FlatTreeControl} from '@angular/cdk/tree';
import {I18NService} from '../../i18n/i18n.service';
import * as _ from 'lodash';
import {EnergyCommunityPlant, EnergyCommunityPod, EnergyCommunityStorage} from 'smag-commons/dto/api';
import {EntityIconPipe} from '../shared.pipe';
import {EntityType} from 'smag-commons/cfg/hierarchy';
import {first, share} from 'rxjs/operators';

export interface ECNetwork {
    network: EnergyCommunityNetwork;
    selectedResourceId?: string;
}

export interface ECSelection {
    resourceId: string; // can be the network itself
}

enum NodeType {
    NETWORK,
    POD,
    PLANT,
    STORAGE
}

interface BaseNode {
    id: string; // resource's id (`null` means network's root)
    type: NodeType;
    name: string;
    icon?: string;
}

// resource node using while building the tree
interface ResourceNode extends BaseNode {
    children: ResourceNode[];
}

// flat resource node with expandable and level information
interface FlatResourceNode extends BaseNode {
    expandable: boolean;
    level: number;
}

@Component({
    selector: 'app-ec-resource-tree',
    templateUrl: './ec-resource-tree.component.html',
    styleUrls: ['./ec-resource-tree.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ECResourceTreeComponent implements OnInit, OnChanges {

    private eip: EntityIconPipe;
    private foundNodes: Set<string>;

    @Input() network: ECNetwork | null;
    @Output() selectionChange: EventEmitter<ECSelection> = new EventEmitter<ECSelection>();

    treeControl: FlatTreeControl<FlatResourceNode, string>;
    dataSource: MatTreeFlatDataSource<ResourceNode, FlatResourceNode>;
    selectedNodeId: string | null;
    searchText: string;

    // exported in HTML
    NodeType = NodeType;

    constructor(private readonly i18n: I18NService,
                private readonly changeDetectorRef: ChangeDetectorRef) {
        this.eip = new EntityIconPipe();
        this.foundNodes = new Set<string>();
        this.treeControl = new FlatTreeControl<FlatResourceNode, string>(node => node.level, node => node.expandable, {
            trackBy: node => node.id
        });
        const treeFlattener = new MatTreeFlattener<ResourceNode, FlatResourceNode, string>((node, level) => mkFlatNode(node, level),
            node => node.level, node => node.expandable, node => node.children);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, treeFlattener);
        this.selectedNodeId = null;
        this.searchText = '';
    }

    ngOnInit() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('network' in changes) {
            if (this.network) {
                // assume that network is to be (re-)loaded if and only if object *reference* has changed;
                // this keeps currently expanded/collapsed nodes upon `selectedResourceId` changes
                // WARNING: this assumes all relevant changes within the network to build a
                //          *new* `EnergyCommunityNetwork` object!
                if (this.network.network !== changes.network?.previousValue?.network) {
                    this.dataSource.data = this.buildTree();
                    this.dataSource._flattenedData.pipe(
                        share(),
                        first()
                    ).subscribe(d => {
                        this.treeControl.dataNodes = d;
                        this.treeControl.expandAll();
                    });
                    this.updateFoundNodes();
                }
                this.selectedNodeId = this.network.selectedResourceId || this.network.network.root.id;
                this.selectionChange.emit({
                    resourceId: this.selectedNodeId
                });
            }
            this.changeDetectorRef.markForCheck();
        }
    }

    get hasSearchText(): boolean {
        const searchText = this.searchText ? this.searchText.trim() : '';
        return !!searchText.length;
    }

    nodeFound(node: FlatResourceNode): boolean {
        return this.foundNodes.has(node.id);
    }

    onChangeSearchText(text: string) {
        this.searchText = text;
        this.updateFoundNodes();
        this.changeDetectorRef.markForCheck();
    }

    onClickResetSearchText() {
        this.searchText = '';
        this.updateFoundNodes();
        this.changeDetectorRef.markForCheck();
    }

    onSelectNode(node: FlatResourceNode) {
        this.selectedNodeId = node.id;
        this.selectionChange.emit({
            resourceId: node.id
        });
    }

    private buildTree(): ResourceNode[] {
        return [
            {
                id: this.network.network.root.id,
                type: NodeType.NETWORK,
                name: this.i18n.translate(this.network.network.root.name),
                icon: null,
                children: this.mkPods(this.network.network.pods)
            }
        ] as ResourceNode[];
    }

    private mkPods(pods: { [id: string]: EnergyCommunityPod }): ResourceNode[] {
        return _.sortBy(_.map(pods, (pod, podId) => ({
            id: podId,
            type: NodeType.POD,
            name: pod.name,
            icon: null,
            children: [
                ...this.mkPlants(pod.plants || {}),
                ...this.mkStorages(pod.storages || {})
            ]
        } as ResourceNode)), pod => pod.name);
    }

    private mkPlants(plants: { [id: string]: EnergyCommunityPlant }): ResourceNode[] {
        return _.sortBy(_.map(plants, (plant, plantId) => ({
            id: plantId,
            type: NodeType.PLANT,
            name: plant.name,
            icon: this.eip.transform({type: this.network.network.entities[plantId].type, sub_type: this.network.network.entities[plantId].sub_type}),
            children: this.mkStorages(plant.storages || {})
        } as ResourceNode)), pod => pod.name);
    }

    private mkStorages(storages: { [id: string]: EnergyCommunityStorage }): ResourceNode[] {
        const storageIcon = this.eip.transform({type: EntityType.ACCUMULATOR});
        return _.sortBy(_.map(storages, (storage, storageId) => ({
            id: storageId,
            type: NodeType.STORAGE,
            name: storage.name,
            icon: storageIcon,
            children: []
        } as ResourceNode)), pod => pod.name);
    }

    private updateFoundNodes() {
        // consider all and only nodes with either a matching parent or child

        this.foundNodes.clear();

        const name = this.searchText ? this.searchText.trim().toLowerCase() : '';

        // network's root is always included
        const networkNode = this.dataSource.data[0];
        if (!networkNode) {
            return;
        }
        this.foundNodes.add(networkNode.id);
        networkNode.children.forEach(node => _check(this, node));

        function _check(self: ECResourceTreeComponent, node: ResourceNode): boolean {
            let found = !name.length || node.name.toLowerCase().includes(name);
            if (found) {
                // node matches: add it along with its children
                _addNode(self, node);
                return true;
            }
            // node does not match: let's see if one of its children does
            node.children.forEach(child => {
                if (_check(self, child)) {
                    found = true;
                }
            });
            if (found) {
                self.foundNodes.add(node.id);
            }
            return found;
        }

        function _addNode(self: ECResourceTreeComponent, node: ResourceNode) {
            self.foundNodes.add(node.id);
            node.children.forEach(child => _addNode(self, child));
        }
    }
}

function mkFlatNode(node: ResourceNode, level: number): FlatResourceNode {
    const flatNode: FlatResourceNode = {
        ...node,
        expandable: !!node.children.length,
        level
    };
    delete flatNode['children'];
    return flatNode;
}

